import { EditNoteOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { memo } from 'react';
import { usePostFolioItemVoidMutation } from '../../../../../api/front-office/roomposting.api.rtk';
import { getColor } from '../../../../../assets/theme';
import LoadingComponent from '../../../../../components/animation/loading-component';
import useToast from '../../../../../hooks/useToast';
import { useRoomPostingContext } from '../../../containers/fo-room_posting.container';
import { RoomPostingVoidItem } from '../../../interface/room-posting-interface';

import ContainerTitle from '@components/ui/container-title';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import useAppContext from '../../../../../hooks/useAppContext';
import { voidRemarksSchema } from '../../../interface/room-posting-schema-interface';

interface FormValues {
  itemRemarks: string;
}

const FORoomPostingItemVoid = ({
  selectedIDs,
  handleVoidedAR,
  handleClose,
  isSpecificBooking,
}: {
  selectedIDs: string;
  handleVoidedAR: (voidARNos: string) => void;
  handleClose: () => void;
  isSpecificBooking?: boolean;
}) => {
  const { primary } = getColor();
  const { toast } = useToast();
  const { useProperty, ledgerRefetch } = useRoomPostingContext();
  const { openSecCodeDialog } = useAppContext();
  // API Void Item
  const [postVoidItemRemarks, { isLoading: isFetching }] =
    usePostFolioItemVoidMutation();
  const methods = useForm<FormValues>({
    resolver: yupResolver(voidRemarksSchema),
    mode: 'onSubmit',
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = methods;

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const { itemRemarks } = data;

    const postVoidItem: RoomPostingVoidItem = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            item_list: [
              {
                identifier_list: selectedIDs,
                void_reason: itemRemarks,
              },
            ],
          },
        },
      ],
    };

    openSecCodeDialog(async (result) => {
      if (result?.data.isValid) {
        try {
          const voidResponse = await postVoidItemRemarks({
            ...postVoidItem,
            invalidateTags: isSpecificBooking,
          }).unwrap();
          // Extract the voidResponse
          if (voidResponse?.data?.[0]?.result) {
            //get the voided ARS
            handleVoidedAR(voidResponse.data[0].result);
          }

          toast({
            severity: 'success',
            message: `Successfully voided item.`,
          });
          ledgerRefetch();
          handleClose();
        } catch (err) {
          toast({ severity: 'error', message: err });
          throw err;
        }
      } else {
        return;
      }
    }, 'del_rc');
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        gap={1}
        sx={{ p: 3, width: '100%' }}
      >
        {isFetching && <LoadingComponent />}
        <Stack>
          <Stack pb={1}>
            <Stack
              sx={{
                color: primary,
                backgroundColor: '#f7f7fb',
                width: '60px',
                height: '60px',
                borderRadius: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <EditNoteOutlined sx={{ fontSize: 26 }} />
            </Stack>

            <ContainerTitle
              title='Void Remarks'
              func={() => {}}
              titleStyle={{
                fontSize: '18px!important',
                fontWeight: '500',
              }}
            />
          </Stack>

          <InputFieldsController
            label='Remarks'
            name='itemRemarks'
            placeholder='Enter void remarks'
            control={control}
            errors={errors}
            multiline
            rows={4}
            helperText={`${watch('itemRemarks')?.length || 0}/100`}
          />
        </Stack>

        <Stack alignItems='flex-end'>
          <Stack direction='row' gap={1}>
            <Button variant='outlined' onClick={handleClose}>
              Cancel
            </Button>
            <Button type='submit' variant='contained'>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export default memo(FORoomPostingItemVoid);
